import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSingleCache } from '@/data/hook/query/v2/useCacheManager';
import { scheduledLessonsQueryKeys } from '@/data/services/querykeys';
import {
  updateScheduledLesson,
  UpdateScheduledLessonPlanItemBody,
} from '@/data/services/scheduledLessonsServices';
import ScheduledLesson from '@/models/ScheduledLesson';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import { scheduledLessonPlanItemsProps } from '@/components/scheduled-lesson/scheduled-lesson-plan-items/ScheduledLessonPlanItems';

export default function useScheduledLessonPlanItemsHandler({
  scheduledLessonId,
  scheduledLessonPlanItems,
}: scheduledLessonPlanItemsProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'scheduledLessonReportModal',
  });

  const { queryKey } = scheduledLessonsQueryKeys.get(scheduledLessonId);
  const cache = useSingleCache<ScheduledLesson>(queryKey);

  const updateMutation = useMutation({
    mutationFn: (planItems: UpdateScheduledLessonPlanItemBody[]) =>
      updateScheduledLesson(scheduledLessonId, {
        scheduledLessonPlanItems: planItems,
      }),
    onSuccess: scheduledLesson => {
      cache.set(scheduledLesson);
      alert.success(t('planItemUpdated'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const toggleHasDone = (planItemId: number) =>
    updateMutation.mutate(
      scheduledLessonPlanItems.map(({ id, hasDone }) => ({
        id,
        hasDone: id === planItemId ? !hasDone : hasDone,
      })),
    );

  return { toggleHasDone, isLoading: updateMutation.isPending };
}
